import React from 'react';

import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Table from '@mui/material/Table';
import { TableContainer } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import swal from 'sweetalert';
import noImage from './../images/noImage.png';

import level_employee from './../images/icons/employee.png';
import level_coordinator from './../images/icons/coordinator.png';
import level_owner from './../images/icons/owner.png';
import level_order from './../images/icons/cashier.png';

import AccountBoxIcon from '@mui/icons-material/AccountBox';
import PasswordIcon from '@mui/icons-material/Password';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PinIcon from '@mui/icons-material/Pin';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import BackOfficeConfig from './../Config';

import Client from './Client';
import Config from './ClientConfig';

import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';

import TokenIcon from '@mui/icons-material/Token';
import UpdateIcon from '@mui/icons-material/Update';
import { FaPlusCircle, FaMinusCircle } from "react-icons/fa";
import CloseIcon from '@mui/icons-material/Close';
import DropDown from './DropDown';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import KImage from './KImage';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#273442",
    color: theme.palette.common.white,
    paddingLeft: "4px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    paddingLeft: "4px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const LocationSelectMenuProps = {
  PaperProps: {
    style: {
      maxHeight: "300px",
    },
  },
};

function toBareBase64(input) {
  let s = String(input ?? '').trim();
  if (!s) return '';
  const comma = s.indexOf(',');
  if (s.startsWith('data:') && comma !== -1) s = s.slice(comma + 1);
  s = s.replace(/^base64,?/i, '').replace(/\s+/g, '');
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(s)) return '';
  const pad = s.length % 4;
  if (pad) s += '='.repeat(4 - pad);
  return s;
}

function usePreloadedSrc(src) {
  const [ready, setReady] = React.useState(false);
  const [okSrc, setOkSrc] = React.useState('');

  React.useEffect(() => {
    setReady(false);
    setOkSrc('');
    if (!src) { setReady(true); return; }

    const s = String(src);
    const isUrlOrPath =
      s.startsWith('/') || s.startsWith('./') || s.startsWith('http://') || s.startsWith('https://');

    if (isUrlOrPath) {
      const im = new Image();
      im.onload = () => { setOkSrc(s); setReady(true); };
      im.onerror = () => { setOkSrc(''); setReady(true); };
      im.src = s;
      return () => { im.onload = null; im.onerror = null; };
    }

    const body = toBareBase64(s) || noImage;
    setOkSrc('data:image/png;base64,' + body);
    setReady(true);
  }, [src]);

  return { ready, okSrc };
}

const MemoKImage = React.memo(function MemoKImage({
  edit,
  src, 
  alt,
  imageText,
  saveBack,
  width = 128,
  height = 128,
}) {
  const srcStr = src;
  const altStr = alt ?? imageText;
  const isUrlOrPath =
    typeof srcStr === 'string' &&
    (srcStr.startsWith('/') || srcStr.startsWith('./') ||
     srcStr.startsWith('http://') || srcStr.startsWith('https://'));

  const { ready, okSrc } = usePreloadedSrc(edit ? null : srcStr);

  if (!ready && !edit) {
    return (
      <div
        style={{ width, height, background: '#e5e7eb', borderRadius: 6 }}
        aria-label="image loading"
      />
    );
  }

  if (edit) {
    let safeSrc = srcStr || noImage;
    if (typeof safeSrc === "string" && safeSrc.startsWith("data:")) {
      const last = safeSrc.lastIndexOf("data:");
      if (last > 0) safeSrc = safeSrc.slice(last);
    }
    if (typeof safeSrc === "string" && !safeSrc.startsWith("data:")) {
      const s = safeSrc.trim();
      const looksLikeBase64 =
        s.length > 50 &&
        !s.startsWith("/") &&
        !s.startsWith("http://") &&
        !s.startsWith("https://") &&
        /^[A-Za-z0-9+/=\s]+$/.test(s);
      if (looksLikeBase64) safeSrc = "data:image/png;base64," + s.replace(/\s+/g, "");
    }
    const imgObj = { data: safeSrc, width, height };

    return (
      <div style={{ display: 'grid', gap: 6 }}>
        <KImage
          edit={edit}
          image={imgObj}
          imageText={altStr}
          saveBack={saveBack}
        />
      </div>
    );
  }

  if (isUrlOrPath) {
    return (
      <img
        src={okSrc || noImage}
        alt={imageText || ''}
        width={width}
        height={height}
        style={{ objectFit: 'contain', display: 'block' }}
      />
    );
  }

  const dataUrl = 'data:image/png;base64,' + (toBareBase64(srcStr) || noImage);
  return (
    <img
      src={dataUrl}
      alt={imageText || ''}
      width={width}
      height={height}
      style={{ objectFit: 'contain', display: 'block' }}
    />
  );
}, (prev, next) =>
  prev.edit === next.edit &&
  prev.src === next.src &&
  prev.imageText === next.imageText &&
  prev.width === next.width &&
  prev.height === next.height &&
  prev.saveBack === next.saveBack
);

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

class LocationRow extends React.Component {
  constructor(props) {
    super(props);

    this.state = { 
      client: new Client ( "none" ),     
      edit: false,
      row: {},
      iref: "",
      somethingHasChanged: false,
      showConfirm: false,
      allowEdit: false
    };

    this.handleEditClick = this.handleEditClick.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.changeType = this.changeType.bind(this);
    this.pushData = this.pushData.bind(this);
    this.showConfirmDialog = this.showConfirmDialog.bind(this);
    this.removeCancel = this.removeCancel.bind(this);
    this.removeConfirm = this.removeConfirm.bind(this);
    this.removeLocation = this.removeLocation.bind(this);
  }

  componentDidMount ( ) {
    if ( this.props ) {
      if ( this.props.dataRow ) {
        this.setState ( { row: this.props.dataRow } );
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
  // If iref is missing, try to recover it from current state/props
  if (!this.state.iref) {
    const fromState = (typeof this.state.row === "string") ? this.state.row : this.state.row?.iref;
    const fromProps = (typeof this.props.dataRow === "string") ? this.props.dataRow : this.props.dataRow?.iref;
    const iref = fromState || fromProps || "";

    if (iref && iref !== prevState.iref) {
      this.setState({ iref });
    }
  }
}

  handleEditClick ( ) {
    let cEdit = !this.state.edit;
    this.setState({ edit: !this.state.edit });
  }

  showConfirmDialog() {
    if (!this.props.allowEdit) return; 
    this.setState({ showConfirm: true });
  }

  removeCancel() {
    this.setState({ showConfirm: false });
  }

  removeConfirm() {
    this.removeLocation();
    this.setState({ showConfirm: false });
  }

  removeLocation() {
    if (!this.props.allowEdit) return; 
    if (!this.state.edit) return;

    const iref = this.state.iref || this.state.row?.iref;
    if (!iref) return;

    const rdata = this.state.client.pushData(
      Config.API_URL_LOCATIONEMPLOYEES_REMOVE,
      { iref }
    );

    if (rdata) {
      if (rdata.status === "OK") {
        this.handleEditClick();
        swal("Success", "Successfully removed location ! (" + rdata.iref + ")", "success", {
          buttons: false,
          timer: 2000,
        }).then(() => {
          if (typeof this.props.employeeUpdated === "function") {
            this.props.employeeUpdated(); // refresh parent
          }
          const locationIref = this.state.row?.location?.iref;
          if (typeof this.props.onLocationRemoved === "function") {
            this.props.onLocationRemoved(locationIref || iref);
          }
          this.setState({ somethingHasChanged: false });
        });
      } else {
        if (rdata.message) this.setState({ errorMessage: rdata.message });
        swal("Error", "Remove location failed !", "error");
      }
    }
  }

  changeType ( e ) {
    let uRow = this.state.row;
    uRow.level = parseInt ( e );
    this.setState ( { row: uRow } );
    this.setState ( { somethingHasChanged: true } );
  }

  pushData ( e ) {
    if ( this.state.edit && this.state.somethingHasChanged ) {      
      let row = this.state.row;
      const iref  = this.state.iref;
      const level = this.state.row?.level;
      let rdata = this.state.client.pushData(Config.API_URL_LOCATIONEMPLOYEES_UPDATE_LEVEL, { iref, level });
      if ( rdata ) {
        if ( rdata.status === "OK" ) {
          let uRow = this.state.row;
          uRow.iref = rdata.iref;
          this.setState({ row: uRow, iref: rdata.iref || iref });
          this.handleEditClick ( );
          swal ( "Success", "Successfully added/modified employee ! (" + rdata.iref + ")", "success", {
            buttons: false,
            timer: 2000,
          })
          .then((value) => {
            //this.props.employeeUpdated ( );
            this.setState ( { somethingHasChanged: false } );            
          });        
        } else {
          if ( rdata.message ) this.setState ( { errorMessage: rdata.message } );
          swal ( "Error", "Add/Update employee failed !", "error" );
        }
      }
    }
  }

  handleSave ( ) {
    this.pushData ( );
  }


  render() {
      let name = "";
      let status = "";
      let locationType = "";
      let relation = "";
      let add_street = "";
      let add_number = "";
      let add_floor = "";
      let add_city = "";
      let add_county = "";
      let add_country = "";
      let locationImage = noImage;
      let companyName = "";
      let companyUID = "";
      let level = 0;
      let levelText = "Employee";

      let row = [];
      if ( this.props.dataRow !== null && this.props.dataRow !== undefined ) {
        row = this.props.dataRow;
      }

      if ( this.state.row ) {
        let row = this.state.row;

        if ( row.level !== undefined ) {
          level = row.level;
          switch ( row.level ) {
            case 0:        
              levelText = "Employee";
              break;
            case 1:
              levelText = "OrdersEmployee";
              break;
            case 2:        
              levelText = "Coordinator";
              break;
            case 3:        
              levelText = "Owner";
              break;
          }
        }

        if ( row.location ) {
          name = row.location.name;
          status = row.location.status;
          locationType = row.location.locationType;
          relation = row.location.relation;
          
          if ( row.location.address ) {
            add_street = row.location.address.street;
            add_number = row.location.address.number;
            add_floor = row.location.address.floor;
            if ( row.location.address.city ) {
              add_city = row.location.address.city.name;
              if ( row.location.address.city.county ) {
                add_county = row.location.address.city.county.name;
                if ( row.location.address.city.county.country ) add_country = row.location.address.city.county.country.name;
              }
            }
          }
  
          let locationImagesObj = row.location.locationImage_location;
          let locationImages = Object.entries ( locationImagesObj );
          let fImage = false;
          locationImages.map ( (image) => {
            if ( fImage === false ) {
              locationImage = image[1].image;
              let fileName = locationImage.replace(/\.[^/.]+$/, "")
              let fileExtensions = locationImage.split(".").pop();
              locationImage = fileName + "_s." + fileExtensions;
              fImage = true;
            } else {
              if ( image[1].isMain === true ) {
                locationImage = image[1].image;
                let fileName = locationImage.replace(/\.[^/.]+$/, "")
                let fileExtensions = locationImage.split(".").pop();
                locationImage = fileName + "_s." + fileExtensions;
              }
            }
          });
  
          if ( row.location.company ) {
            companyName = row.location.company.name;
            companyUID = row.location.company.cuid;
          }
  
        }
      }

      let showSave = false;
      if ( this.state.edit && this.state.somethingHasChanged )
        showSave = true;
    
    return (
      <StyledTableRow key={row.iref} sx={{padding: "1px"}}>

        <StyledTableCell align="left" sx={{padding: "1px"}}>        
          <Box
            component="img"
            sx={{
              height: 48,
              width: 48,
              maxHeight: { xs: 233, md: 167 },
              maxWidth: { xs: 350, md: 250 },
              border: '1px dashed grey'
            }}
            alt={name}
            src={locationImage}
          />
        </StyledTableCell>

        <StyledTableCell component="td" scope="row" sx={{padding: "1px"}}>
          {name}
        </StyledTableCell>

        <StyledTableCell component="td" scope="row" sx={{padding: "1px"}}>         
          {companyName} ({companyUID})
        </StyledTableCell>

        <StyledTableCell component="td" scope="row" sx={{padding: "1px"}}>
          {add_street}, n. {add_number}, et. {add_floor}, {add_city}, {add_county}, {add_country}
        </StyledTableCell>

        <StyledTableCell component="td" scope="row" sx={{padding: "1px"}}>
          {this.state.edit ?
            <DropDown
              name="Employee Type"
              value={level}
              doptions={BackOfficeConfig.EMPLOYEE_TYPES}
              hasIcons={false} 
              minWidth={120}
              onChangeFunction={this.changeType}
              size="small"
            />
            : levelText
          }
          
        </StyledTableCell>

        <StyledTableCell align="right" sx={{padding: "1px"}}>
          <IconButton aria-label="settings" onClick={this.handleEditClick}>
            <EditIcon sx={{ color: this.state.edit ? "#8f241d" : "#e3e3e5" }} />
          </IconButton>
          {this.state.edit && showSave ?
            <IconButton aria-label="settings" onClick={this.handleSave}>
              <SaveIcon sx={{ color: "#4287f5" }} />
            </IconButton>
          : ""}
          {this.state.edit ?
            <IconButton aria-label="remove" onClick={this.showConfirmDialog}>
              <DeleteForeverIcon sx={{ color: "#8f241d" }} />
            </IconButton>
          : ""}
          <Dialog
            open={this.state.showConfirm}
            TransitionComponent={Transition}
            keepMounted
            onClose={this.removeCancel}
            aria-describedby="alert-dialog-slide-description"
          >
            <DialogTitle>{"Remove location ?"}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-slide-description">
                This action will not delete the location, it will just remove the location from the employee.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.removeCancel}>No</Button>
              <Button onClick={this.removeConfirm}>Yes</Button>
            </DialogActions>
          </Dialog>
        </StyledTableCell>

      </StyledTableRow>
    );
  }
}

class Employee extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      client: new Client ( "none" ),
      expanded: false,
      edit: false,
      somethingHasChanged: false,
      saveButtonVisible: false,
      //combo_mainImage: null,
      employee: {},
      personsSearch: [],
      personDraft: {
        city: {
          iref: "city000000",
          name: "City",
          county: {
            iref: "14f2aa40c2",
            name: "County",
            country: {
              iref: "country061",
              name: "Country"
            }
          }
        },
        contractId: "",
        firstName: "",
        lastName: "",
        gender: 9,
        image: ""
      },
      countries: {},
      counties: {},
      cities: {},
      contractIdValid: false,
      firstNameValid: false,
      lastNameValid: false,
      countryValid: false,
      countyValid: false,
      cityValid: false,
      usernameValid: false,
      passwordValid: false,
      cardValid: false,
      pinValid: false,
      locations: [],
      locationNames: [],
      locationsIref: [],
      locationsOnlyMode: false,
      locationsBackup: null,
      locationNamesBackup: null,
      allowEdit: false
    };

    this.handleExpandClick = this.handleExpandClick.bind(this);
    this.handleEditClick = this.handleEditClick.bind(this);
    this.getElementByName = this.getElementByName.bind(this);
    this.personChange = this.personChange.bind(this);
    this.personSearch = this.personSearch.bind(this);
    this.changeValue = this.changeValue.bind(this);
    this.pushData = this.pushData.bind(this);
    this.changeLocation = this.changeLocation.bind(this);    
    this.getLocations = this.getLocations.bind(this);
    this.changeEmployeeLevel = this.changeEmployeeLevel.bind(this);
    this.changePersonDraftValue = this.changePersonDraftValue.bind(this);
    this.getImage = this.getImage.bind(this);
    this.addCountry = this.addCountry.bind(this);
    this.addCounty  = this.addCounty.bind(this);
    this.addCity    = this.addCity.bind(this);
  }


  componentDidMount() {
    this.loadCountries();
    if ( Object.entries(this.props.employee).length === 0 ) {
      if (this.props.allowEdit === true)
        this.setState ({allowEdit: true}, this.handleEditClick (this.props.allowEdit));
    } else {
      if (this.props)
        if (this.props.allowEdit === true)
          this.setState ({allowEdit: true});
      if ( this.props.employee !== null ) {
        let uEmployee = this.props.employee;  
        if (uEmployee.allowEdit === true)
          this.setState ({allowEdit: true});
        this.setState ( { employee: uEmployee } );

        if ( uEmployee.username.length > 3 )
          this.setState ( { usernameValid: true } );
        if ( uEmployee.password.length >= 6 )
          this.setState ( { passwordValid: true } );
        if ( uEmployee.card.length >= 8 )
          this.setState ( { cardValid: true } );
        if ( uEmployee.pin.length >= 4 )
          this.setState ( { pinValid: true } );


        if ( this.props.employee.employeeLocation_employee ) {
          let eloc = this.props.employee.employeeLocation_employee;
          if ( eloc.length > 0 ) {
            let els = [];
            eloc.map((el) => {
              // let newLocation = "[" + el.location.iref + "][" + el.location.address.city.county.name + ", " + el.location.address.city.name + "] " + el.location.name;
              let newLocation = "[" 
                + (el.location?.iref ?? "") + "]["
                + (el.location?.address?.city?.county?.name ?? "") + ", "
                + (el.location?.address?.city?.name ?? "") + "] "
                + (el.location?.name ?? "");
              els.push ( newLocation );
            });
            this.setState ( { locationNames: els } );
          }
        }
      }
    }
  }

  componentDidUpdate ( oldProps ) {
    if ( this.props !== oldProps ) {
      if ( this.props.employee !== null ) {
        let uEmployee = this.props.employee;        
        this.setState ( { employee: uEmployee } );
      }
    }
  }

  handleExpandClick ( ) {
    this.setState({ expanded: !this.state.expanded });
  }

  toggleLocationsOnlyMode = () => {
    this.setState(
      (prev) => {
        const next = !prev.locationsOnlyMode;

        return {
          locationsOnlyMode: next,
          expanded: next ? true : prev.expanded,

          // backup only when entering
          locationsBackup: next
            ? (prev.employee?.locations ? [...prev.employee.locations] : null)
            : prev.locationsBackup,

          locationNamesBackup: next
            ? [...(prev.locationNames || [])]
            : prev.locationNamesBackup,
        };
      },
      () => {
        // load options when entering locations-only
        if (
          this.state.locationsOnlyMode &&
          (!this.state.locations || this.state.locations.length === 0)
        ) {
          this.getLocations();
        }
      }
    );
  };

  cancelLocationsOnlyMode = () => {
    const restoredEmployee = { ...this.state.employee };
    if (this.state.locationsBackup) restoredEmployee.locations = this.state.locationsBackup;

    this.setState({
      locationsOnlyMode: false,
      expanded: false,               // optional but usually desired
      somethingHasChanged: false,
      employee: restoredEmployee,
      locationNames: this.state.locationNamesBackup || [],
      locationsBackup: null,
      locationNamesBackup: null,
    });
  };

  changeEmployeeLevel(value) {
    const v = parseInt(value);
    const employee = { ...(this.state.employee || {}), level: v };
    this.setState({ employee, somethingHasChanged: true });
  }

  getLocations() {
    let queryData = {
      "orderBy_county": true,
      "orderBy_name": true
    };
    let data = this.state.client.getData(Config.API_URL_LOCATIONS, queryData);
    // console.log(data);
    if (data) {
      if (data.locations) {
        let locations = [];
        data.locations.map((location) => {
          if (location?.status !== 1) return;
          const county = location?.address?.city?.county?.name || "";
          const city = location?.address?.city?.name || "";
          const iref = location?.iref || "";
          const name = location?.name || "";

          let newLocation = `[${iref}][${county}${county && city ? ", " : ""}${city}] ${name}`;
          locations.push(newLocation);
        });
        this.setState({ locations: locations });
      }
      this.setState({ employees: data.employees });
    }
  }

  initGeoData() {
    const d = this.state.personDraft || {};
    const countryId = d.city?.county?.country?.iref || "";
    const countyId = d.city?.county?.iref || "";

    // load counties for selected country
    if (countryId) {
      let data = this.state.client.getData(Config.API_URL_COUNTY, {
        country: countryId,
        orderBy_Name: true
      });
      this.setState({ counties: data ? data.counties : {} });
    } else {
      this.setState({ counties: {} });
    }

    // load cities for selected county
    if (countyId) {
      let data = this.state.client.getData(Config.API_URL_CITY, {
        county: countyId,
        orderBy_Name: true
      });
      this.setState({ cities: data ? data.cities : {} });
    } else {
      this.setState({ cities: {} });
    }
  }

  getImage(newImage) {
    if (!newImage) return;

    const d = { ...(this.state.personDraft || {}) };
    d.image = newImage;

    this.setState({ personDraft: d, somethingHasChanged: true });
  }

  addCountry(e) {
    const d = { ...(this.state.personDraft || {}) };

    d.city = {
      iref: "",
      name: "",
      county: {
        iref: "",
        name: "",
        country: { iref: e, name: "" }
      }
    };

    this.setState({
      personDraft: d,
      somethingHasChanged: true,
      cities: {}
    });

    const data = this.state.client.getData(Config.API_URL_COUNTY, {
      country: e,
      orderBy_Name: true
    });

    this.setState({ counties: data ? data.counties : {} });
  }

  loadCountries() {
    const queryData = { orderBy_Name: true };
    const data = this.state.client.getData(Config.API_URL_COUNTRY, queryData);

    const countriesObj = (data && data.countries) ? data.countries : {};
    this.setState({ countries: countriesObj }, () => {
      // auto-select if only one country and draft has no country yet
      const map = {};
      Object.keys(countriesObj).forEach(k => {
        const c = countriesObj[k];
        map[c.iref] = c.name;
      });

      const only = Object.keys(map).length === 1 ? Object.keys(map)[0] : "";
      const current = this.state.personDraft?.city?.county?.country?.iref || "";

      if (only && !current && !this.state.employee?.person?.iref) {
        this.addCountry(only);
      }
    });
  }

  addCounty(e) {
    const d = { ...(this.state.personDraft || {}) };

    if (!d.city) d.city = {};
    if (!d.city.county) d.city.county = {};
    if (!d.city.county.country) d.city.county.country = {};

    d.city.iref = "";
    d.city.county.iref = e;

    this.setState({
      personDraft: d,
      somethingHasChanged: true
    });

    const data = this.state.client.getData(Config.API_URL_CITY, {
      county: e,
      orderBy_Name: true
    });

    this.setState({ cities: data ? data.cities : {} });
  }

  addCity(e) {
    const d = { ...(this.state.personDraft || {}) };

    if (!d.city) d.city = {};
    d.city.iref = e;

    this.setState({
      personDraft: d,
      somethingHasChanged: true
    });
  }

  changePersonDraftValue(e) {
    const id = e.target.id;
    const value = (e.target.value ?? "").toString();

    const d = { ...(this.state.personDraft || {}) };

    switch (id) {
      case "pd_contractId":
        d.contractId = value;
        this.setState({ contractIdValid: value.trim().length > 0 });
        break;
      case "pd_firstName":
        d.firstName = value;
        this.setState({ firstNameValid: value.trim().length > 1 });
        break;
      case "pd_lastName":
        d.lastName = value;
        this.setState({ lastNameValid: value.trim().length > 1 });
        break;
      default:
        break;
    }

    this.setState({ personDraft: d, somethingHasChanged: true });
  }

  changeBirthdayDraft = (e) => {
    if (e) {
      const year = e.$y;
      const imonth = parseInt(e.$M) + 1;
      const month = String(imonth).padStart(2, '0');
      const iday = parseInt(e.$D);
      const day = String(iday).padStart(2, '0');
      const bday = `${year}-${month}-${day}`;

      const d = { ...(this.state.personDraft || {}) };
      d.birthday = bday;

      this.setState({ personDraft: d, somethingHasChanged: true });
    }
  }

  addGenderDraft = (e) => {
    const d = { ...(this.state.personDraft || {}) };
    d.gender = parseInt(e);
    this.setState({ personDraft: d, somethingHasChanged: true });
  };

  handleEditClick = (forceEdit = false) => {
    if (!this.state.allowEdit && !forceEdit) return;

    const cEdit = !this.state.edit;
    this.setState(
      {
        edit: cEdit,
        expanded: cEdit,
        saveButtonVisible: cEdit
      },
      () => {
      if (cEdit) {
        this.getLocations();
        if (!this.state.employee?.person?.iref) {
          this.initGeoData();
        }
      }
    }
    );
  };

  getElementByName ( elements, name ) {    
    let e = {
      "iref" : null,
      "name" : "X",
      "image" : "X",
      "image_off" : "X"
    };
    if ( elements !== null && elements !== undefined ) {
      for ( var i = 0; i < elements.length; i++ ) {
        let el = elements[ i ];
        if ( el.length === 2 ) {
          if ( el[ 1 ].name === name ) e = el[ 1 ];
        } else {
          if ( el[ 0 ].name === name ) e = el[ 0 ];
        }        
      }
    }
    return e;    
  }

  personSearch ( e ) {
    //console.log ( e.target.value );
    if ( e.target.value.length > 3 ) {
      let stringLength = e.target.value.length;
      let limit = (stringLength - 2) * 20;
      let queryData = {
        "nameLike" : e.target.value,
        "notLinked_employee": true,
        "notLinked_user": true,
        "limit": limit
      };
      let data = this.state.client.filterPersons ( queryData );
      let newpersons = [];
      let persons = [];
      Object.keys(data.persons).forEach(key => {
        let fData = data.persons[ key ];
        let lineText = fData.iref + " - " + fData.firstName + " " + fData.lastName + " (" + fData.email + ", " + fData.phone + ")";
        let newObj = { key: fData.iref, label: lineText };
        newpersons.push ( newObj );
        persons.push ( fData );
        //counties [ fData.iref ] = fData.name;      
      });

      this.setState ( { personsSearch: newpersons });
    }
  }

  personChange ( e ) {
    let text = e.target.textContent;
    let repIref = text.substring ( 0, text.indexOf(" - ") );
    let queryData = { };
    let data = this.state.client.getData ( Config.API_URL_PERSON + repIref, queryData );
    let uEmployee = this.state.employee;
    uEmployee.person = data;
    this.setState ( { employee: uEmployee } );    
    this.setState ( { somethingHasChanged: true } );
  }

  changeValue ( e ) {
    let id = null;
    if ( e.target.id !== null ) id = e.target.id;
    let value = null;
    if  ( e.target.value !== null ) value = e.target.value;
    if ( id !== null) {
      let uEmployee = this.state.employee;
      switch ( id ) {
        case "username":
          uEmployee.username = value;
          if ( value.length > 3 ) this.setState ( { usernameValid: true } );
          else this.setState ( { usernameValid: false } );
          break;
        case "password":
          uEmployee.password = value;
          if ( value.length >= 4 || value.length === 0 ) this.setState ( { passwordValid: true } );
          else this.setState ( { passwordValid: false } );
          break;
        case "card":
          uEmployee.card = value;
          if ( value.length >= 8 ) this.setState ( { cardValid: true } );
          else this.setState ( { cardValid: false } );
          break;
        case "pin":
          uEmployee.pin = value;
          if ( value.length >= 4  || value.length === 0 ) this.setState ( { pinValid: true } );
          else this.setState ( { pinValid: false } );
          break;
        case "uSize":
          uEmployee.uSize = value;
          break;
        case "isActive":
          uEmployee.isActive = !!e.target.checked;
          break;
      }
      this.setState ( { employee: uEmployee } );
      this.setState ( { somethingHasChanged: true } );
    }

  }

  handleLocationRemoved = (removedId) => {
    if (!removedId) return;

    this.setState((prev) => {
      const emp = prev.employee || {};
      const list = Array.isArray(emp.locations) ? emp.locations : [];
      const el = Array.isArray(emp.employeeLocation_employee) ? emp.employeeLocation_employee : [];
      const names = Array.isArray(prev.locationNames) ? prev.locationNames : [];

      return {
        employee: {
          ...emp,
          locations: list.filter((x) => {
            if (typeof x === "string") return x !== removedId;
            const relIref = x?.iref;
            const locIref = x?.location?.iref;
            return relIref !== removedId && locIref !== removedId;
          }),

          employeeLocation_employee: el.filter((x) => {
            const relIref = x?.iref;
            const locIref = x?.location?.iref;
            return relIref !== removedId && locIref !== removedId;
          }),
        },

        locationNames: names.filter(
          (s) => typeof s === "string" && !s.startsWith(`[${removedId}]`)
        ),
      };
    });
  };

  pushData(e) {
    if (
      ((this.state.edit && this.state.usernameValid && this.state.passwordValid && this.state.cardValid && this.state.pinValid) ||
        this.state.locationsOnlyMode) &&
      this.state.somethingHasChanged
    ) {
      let employee = this.state.employee;

      if (employee.person)
        if (employee.person.address)
          employee.person.address = {};

      if (employee.locations) {
        for (var l of employee.locations) {
          if (l.location) {
            if (l.location.address) l.location.address = {};
            if (l.location.company) l.location.company = {};
          }
        }
      }

      if (!employee.person || !employee.person.iref) {
        const d = this.state.personDraft || {};

        employee.personDraft = {
          contractId: d.contractId || "",
          firstName: d.firstName || "",
          lastName: d.lastName || "",
          city: d.city?.iref || "",
          county: d.city?.county?.iref || "",
          country: d.city?.county?.country?.iref || "",
          birthday: d.birthday || "",
          gender: (d.gender !== undefined && d.gender !== null) ? d.gender : 9,
          image: d.image || ""
        };

        employee.person = {};
      }

      let rdata = this.state.client.pushData(Config.API_URL_EMPLOYEES_ADD, employee);
      if (rdata) {
        if (rdata.status === "OK") {
          let uEmployee = this.state.employee;
          uEmployee.iref = rdata.iref;
          this.setState({ employee: uEmployee });

          if (this.state.locationsOnlyMode) {
            this.setState({ locationsOnlyMode: false, expanded: false });
          } else {
            this.handleEditClick();
          }

          swal("Success", "Successfully added/modified employee ! (" + rdata.iref + ")", "success", {
            buttons: false,
            timer: 2000,
          }).then((value) => {
            this.props.employeeUpdated();
            this.setState({ somethingHasChanged: false });
          });
        } else {
          if (rdata.message) this.setState({ errorMessage: rdata.message });
          swal("Error", rdata.message || "Add/Update employee failed!", "error");
        }
      }
    }
  }

  changeLocation ( e ) {
    let uLocationNames = this.state.locationNames;
    let value = e.target.value;
    let newValue = typeof value === 'string' ? value.split(',') : value;
    

    let locationsIrefs = [];
    newValue.map((v) => {
      let c1 = v.indexOf ( "[" ) + 1;
      let c2 = v.indexOf ( "]" );
      let iref = v.substring ( c1, c2 );
      locationsIrefs.push ( iref );
    });

    let uEmployee = this.state.employee;
    uEmployee.locations = locationsIrefs;
    this.setState ( { locationNames: newValue } );
    this.setState ( { employee: uEmployee } );
    this.setState ( { somethingHasChanged: true } );
  }

  render() {

    let iref = "";
    let employeeImage = noImage;
    let level = 9;
    let username = "";
    let password = "********";
    let card = "";
    let pin = "********";
    let tokenValid = "";
    let username_pass = "";
    let card_pin = "";

    let firstName = "";
    let lastName = "";
    let phone = "";
    let email = "";
    let birthday = "";
    let updateKey = "";

    let add_name = "";
    let add_postalCode = "";
    let add_street = "";
    let add_number = "";
    let add_entrance = "";
    let add_floor = "";
    let add_complement = "";
    let add_city = "";
    let add_county = "";
    let add_country = "";
    let modifiedOn = "";
    let allData ="";
    let uSize = "";
    let isActive = false;
    let empLevel = 0;
    let empLevelText = "Employee";

    if ( this.state.employee ) {
      let employee = this.state.employee;
      if ( employee.iref ) iref = employee.iref;
      if ( employee.level !== undefined ) level = parseInt ( employee.level );
      if ( employee.username ) username = employee.username;
      if ( employee.uSize ) uSize = employee.uSize;
      if (employee.isActive !== undefined) isActive = !!employee.isActive;
      if (employee.level !== undefined) {
        empLevel = parseInt(employee.level);
        switch (empLevel) {
          case 1: empLevelText = "OrdersEmployee"; break;
          case 2: empLevelText = "Coordinator"; break;
          case 3: empLevelText = "Owner"; break;
          default: empLevelText = "Employee";
        }
      }
      if ( this.state.edit && employee.password ) {
        if ( employee.password.startsWith ( "pbkdf2_sha256" ) )
          password = "********";
        else
          password = employee.password;
      }
      if ( employee.card ) card = employee.card;
      if ( this.state.edit && employee.pin ) {
        if ( employee.pin.startsWith ( "pbkdf2_sha256" ) )
          pin = "********";
        else
          pin = employee.pin;
      }
      if ( employee.tokenValid !== undefined ) tokenValid = employee.tokenValid;
      if ( employee.modifiedOn ) modifiedOn = employee.modifiedOn;
      //if ( employee.image ) employeeImage = employee.image;

      username_pass = "Username :" + username + ", Password:" + password;
      card_pin = "Card: " + card + ", PIN: " + pin;

      if ( employee.person ) {
        firstName = employee.person.firstName;
        lastName = employee.person.lastName;
        phone = employee.person.phone;
        email = employee.person.email;
        birthday = employee.person.birthday;
        updateKey = employee.person.updateKey;

        if ( employee.person.image ) {
          employeeImage = employee.person.image;
          let fileName = employeeImage.replace(/\.[^/.]+$/, "")
          let fileExtensions = employeeImage.split(".").pop();
          employeeImage = fileName + "_s." + fileExtensions;
        }

        if ( employee.person.address ) {
          let add = employee.person.address;  
          add_name = add.name;
          add_postalCode = add.postalCode;
          add_street = add.street;
          add_number = add.number;
          add_entrance = add.entrance;
          add_floor = add.floor;
          add_complement = add.complement;
    
          if ( add.city ) {
            add_city = add.city.name;
            if ( add.city.county ) {
              add_county = add.city.county.name;
              if ( add.city.county.country ) add_country = add.city.county.country.name;
            }
          }        
        }
      }

      if (employee.locations) {
        const sorted = [...employee.locations].sort((a, b) =>
          (a?.location?.name || "").localeCompare(b?.location?.name || "", "ro", { sensitivity: "base" })
        );
        allData = sorted.map((row, i) => (
          <LocationRow
            key={(typeof row === "string" ? row : row?.iref) || `loc-${i}`}
            dataRow={row}
            allowEdit={this.state.allowEdit}
            employeeUpdated={this.props.employeeUpdated}
            onLocationRemoved={this.handleLocationRemoved}
          />
        ));
      }
    }

    let employeeName = firstName + " " + lastName;
    let employeeContact = "";
    if ( phone !== "null" && phone !== null ) employeeContact = phone + ", " + email;
    else employeeContact = email;

    let levelIcon = noImage;
    let levelText = "Undefined";
    switch ( level ) {
      case 0: levelIcon = level_employee; levelText = "Employee"; break;
      case 1: levelIcon = level_coordinator; levelText = "OrdersEmployee"; break;
      case 2: levelIcon = level_owner; levelText = "Coordinator"; break;
      case 3: levelIcon = level_order; levelText = "Owner"; break;
    }

    let location_status_alt = "";
    let location_status_image = noImage;

    let personsSearch = [ ];
    if ( this.state.personsSearch ) personsSearch = this.state.personsSearch;

    let hasPerson = false;    
    if ( this.state.employee.person && 'iref' in this.state.employee.person ) hasPerson = true;

    const existingPerson = this.state.employee?.person;
    const d = this.state.personDraft || {};
    const personFullName =
      (existingPerson?.firstName || d.firstName || "") + " " + (existingPerson?.lastName || d.lastName || "");
    const personImage =
      d.image || existingPerson?.image || noImage;
    const canEditPersonImage = this.state.edit && !this.state.locationsOnlyMode && !existingPerson?.iref;

    let countriesValue = "";
    let countries = {};
    let countriesObj = {};
    if (this.state.countries) countriesObj = this.state.countries;
    Object.keys(countriesObj).forEach(key => {
      let fData = countriesObj[key];
      countries[fData.iref] = fData.name;
    });
    if (Object.keys(countries).length === 1) countriesValue = Object.keys(countries)[0];

    let counties = {};
    let countiesObj = {};
    if ( this.state.counties ) countiesObj = this.state.counties;
    Object.keys(countiesObj).forEach(key => {
      let fData = countiesObj[ key ];
      counties[ fData.iref ] = fData.name;      
    });

    let cities = {};
    let citiesObj = {};
    if ( this.state.cities ) citiesObj = this.state.cities;
    Object.keys(citiesObj).forEach(key => {
      let fData = citiesObj[ key ];
      cities[ fData.iref ] = fData.name;      
    });

    let address_country = "Country";
    let address_country_select = "";
    let address_county = "County";
    let address_county_select = "";
    let address_city = "City";
    let address_city_select = "";

    if (this.state.personDraft) {
      const uCity = this.state.personDraft.city;
      if (uCity) {
        address_city_select = uCity.iref || "";
        address_city = uCity.name || "City";

        if (uCity.county) {
          address_county_select = uCity.county.iref || "";
          address_county = uCity.county.name || "County";

          if (uCity.county.country) {
            address_country_select = uCity.county.country.iref || "";
            address_country = uCity.county.country.name || "Country";
          }
        }
      }
    }

    const genders = BackOfficeConfig.GENDER_TYPES_WITH_ICONS || {};
    let gender = 9;
    if (this.state.employee?.person?.gender !== undefined) {
      gender = this.state.employee.person.gender;
    } else if (this.state.personDraft?.gender !== undefined) {
      gender = this.state.personDraft.gender;
    }

    gender = parseInt(gender);
    if (isNaN(gender)) gender = 9;
    const genderSelectValue = genders[gender] ? gender : "";

    let genderIcon = noImage;
    let genderName = "Undefined";
    let gendersWithIcons = BackOfficeConfig.GENDER_TYPES_WITH_ICONS;

    if (gender !== undefined) {
      if (gender === 0 || gender === 1 || gender === 2 || gender === 9) {
        genderIcon = gendersWithIcons[gender]?.image || noImage;
        genderName = gendersWithIcons[gender]?.name || "Undefined";
      } else {
        genderIcon = noImage;
        genderName = "Unknown";
      }
    }

    let saveButtonVisible = false;
    if (
      ((this.state.edit && this.state.usernameValid && this.state.passwordValid && this.state.cardValid && this.state.pinValid) ||
        this.state.locationsOnlyMode) &&
      this.state.somethingHasChanged
    ) saveButtonVisible = true;

    let locations = [ ];
    if ( this.state.locations ) locations = this.state.locations;

    let locationNames = [];
    if (this.state.locationNames.length > 0) {
      locationNames = [...this.state.locationNames].sort((a, b) => {
        const extractName = (s) => {
          const parts = s.split("]");
          return parts[parts.length - 1].trim(); // text after the last ']'
        };

        return extractName(a).localeCompare(extractName(b), "ro", { sensitivity: "base" });
      });
    }

    let userMargin = 0;
    let cardMargin = 0;
    if ( this.state.edit ) {
      userMargin = 2;
      cardMargin = 3;
    }
    

    return (
      /* , display: 'flex' */
    <Card sx={{ borderRadius: '16px', m: 1 }}>

      <Box sx={{ display: 'block', flexDirection: 'row', width: "100%" }}>
        <CardHeader
          avatar={
            <Box sx={{ display: 'flex', flexDirection: 'row', width: "100%" }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%" }}>
                <Avatar alt={levelText} src={levelIcon} title={levelText}>
                </Avatar>  
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%" }}>
              <Typography variant="subtitle1" color="text.secondary">
                {iref}
              </Typography>
              </Box>
            </Box>
                    
          }        
          action={            
            <IconButton aria-label="settings" onClick={this.handleEditClick}>
              <EditIcon sx={{ color: this.state.edit ? "#8f241d" : "#e3e3e5" }}/>
            </IconButton>
          }
          title={employeeName}
          subheader={employeeContact}
          titleTypographyProps={{variant:'h5' }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', width: "100%" }}>

        <Box sx={{ display: 'block', flexDirection: 'column', width: '20%'}}>
          <MemoKImage
            edit={canEditPersonImage}
            src={personImage}
            imageText={personFullName}
            saveBack={this.getImage}
            width={128}
            height={128}
          />
        </Box>
        
        <Box sx={{ flexDirection: 'column', display: "flex", width: '30%', justifyContent:"left" }}>
          <Box sx={{ flexDirection: 'column', display: "flex", width: '30%', justifyContent:"flex-start", alignItems:"flex-start" }}>
            {this.state.edit && !this.state.locationsOnlyMode ? (
              <FormControlLabel
                label="Active" 
                labelPlacement="start" 
                sx={{
                  m: 0,
                  '& .MuiFormControlLabel-label': { color: 'text.secondary', fontSize: '0.875rem', marginRight: 1 },
                  '& .MuiCheckbox-root': { p: 0.5 }
                }}
                control={
                  <Checkbox
                    id="isActive"
                    color="success"
                    checked={isActive}
                    onChange={this.changeValue}
                  />
                }
              />
            ) : (
              <FormControlLabel
                label="Active"
                labelPlacement="start"
                sx={{
                  m: 0,
                  '& .MuiFormControlLabel-label': { color: 'text.secondary', fontSize: '0.875rem', marginRight: 1 },
                  '& .MuiCheckbox-root': { p: 0.5 }
                }}
                control={
                  <Checkbox
                    color="success"
                    checked={isActive}
                    disabled
                  />
                }
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', width: 280, mt: 1, justifyContent:"flex-start", alignItems:"center" }}>
            {this.state.edit && !this.state.locationsOnlyMode ? (
              <DropDown
                name="Level"
                value={empLevel}
                doptions={BackOfficeConfig.EMPLOYEE_TYPES}
                hasIcons={false}
                width={180}
                onChangeFunction={this.changeEmployeeLevel}
                size="small"
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Level: {empLevelText}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'block', flexDirection: 'row', textAlign: "start", justifyContent:"left" }}>
            {this.state.edit && !this.state.locationsOnlyMode && !this.state.employee?.person?.iref ? (
              <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
                <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "nowrap" }}>
                    <TextField
                      id="pd_contractId"
                      label="Contract ID"
                      value={this.state.personDraft.contractId || ""}
                      onChange={(e) => {
                        const v = (e.target.value || "").replace(/\D/g, "").slice(0, 5);
                        const fakeEvent = { target: { id: "pd_contractId", value: v } };
                        this.changePersonDraftValue(fakeEvent);
                      }}
                      error={!this.state.contractIdValid}
                      size="small"
                      sx={{ width: 120 }}
                      inputProps={{
                        maxLength: 5,
                        inputMode: "numeric",
                        pattern: "[0-9]*"
                      }}
                    />
                    <TextField
                      id="pd_firstName"
                      label="First name"
                      value={this.state.personDraft.firstName || ""}
                      onChange={this.changePersonDraftValue}
                      error={!this.state.firstNameValid}
                      inputProps={{maxLength:"32"}}
                      size="small"
                      sx={{ width: 220 }}
                    />
                    <TextField
                      id="pd_lastName"
                      label="Last name"
                      value={this.state.personDraft.lastName || ""}
                      onChange={this.changePersonDraftValue}
                      error={!this.state.lastNameValid}
                      inputProps={{maxLength:"32"}}
                      size="small"
                      sx={{ width: 220 }}
                    />
                  </Box>

                  {/* Gender + Birthday (compact row) */}
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    { this.state.edit ?
                      <DropDown
                        name="Gender"
                        value={genderSelectValue}
                        doptions={genders}
                        hasIcons={true}
                        width={170}
                        onDark={false}
                        onChangeFunction={this.addGenderDraft}
                        sx={{ marginLeft: 1 }}
                      />
                      : 
                      <Avatar alt={genderName} src={genderIcon} title={genderName} sx={{ height: "36px", width: "36px" }} />
                    }

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Birthday"
                        inputFormat="YYYY-MM-DD"
                        value={this.state.personDraft.birthday || null}
                        onChange={this.changeBirthdayDraft}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{ width: 170 }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Box>

                  {/* Country / County / City row */}
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", mt: 0.5 }}>
                    <DropDown
                      name="Country"
                      value={countries[address_country_select] ? address_country_select : ""}
                      doptions={countries}
                      hasIcons={false}   
                      width={220}
                      onDark={false}
                      onChangeFunction={this.addCountry}
                    />
                    <DropDown
                      name="County"
                      value={counties[address_county_select] ? address_county_select : ""}
                      doptions={counties}
                      hasIcons={false}   
                      width={220}
                      onDark={false}
                      onChangeFunction={this.addCounty}
                    />
                    <DropDown
                      name="City"
                      value={cities[address_city_select] ? address_city_select : ""}
                      doptions={cities}
                      hasIcons={false}   
                      width={220}
                      onDark={false}
                      onChangeFunction={this.addCity}
                    />
                  </Box>
                </Box>
              </Box>
            ) : null}
          </Box>
          <Box sx={{ display: 'block', textAlign: "start", justifyContent: "left" }}>
            {this.state.edit && !this.state.locationsOnlyMode ? (
              <TextField
                id="uSize"
                label="Uniform size"
                value={uSize || ''}
                variant="outlined"
                onChange={this.changeValue}
                onFocus={event => event.target.select()}
                inputProps={{ maxLength: "16" }}
                sx={{ width: 120, marginTop: 1 }}
                size="small"
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Uniform size: {(uSize || '').toUpperCase()}
              </Typography>
            )}
          </Box>

          {!this.state.edit ?
            <Box sx={{ display: 'block', flexDirection: 'row', textAlign: "start", justifyContent:"left" }}>
              {/* <CardActions disableSpacing sx={{display: "flex", justifyContent: "left" }}>
                <IconButton aria-label="Employee of the month" title="Employee of the month">       
                  <Avatar alt="Employee of the month" src={employee_month} sx={{ width: 48, height: 48 }}/>
                </IconButton>
                <IconButton aria-label="Employee of the month" title="Employee of the month">       
                  <Avatar alt="Employee of the month" src={employee_month2} sx={{ width: 48, height: 48 }}/>
                </IconButton>
              </CardActions> */}
            </Box>
            : ""
          }
        </Box>

        
        <Box sx={{ flexDirection: 'column', justifyContent:"right", width: '50%' }}>
          {!this.state.edit ?
            <CardActions disableSpacing sx={{display: "block", textAlign: "end"}}>
              <b>{add_name}</b><br/>
              {add_street}, n. {add_number}, Et. {add_floor}, {add_complement}<br/>
              {add_city}, {add_county}, {add_country}
            </CardActions>
            : ""
          }
          <CardActions disableSpacing sx={{display: "block", textAlign: "end"}}>
            <AccountBoxIcon fontSize="small" color="primary" sx={{ marginTop: userMargin }}/>&nbsp;
            {this.state.edit && !this.state.locationsOnlyMode ?
              <TextField
                id="username"
                label="Username"
                value={username || ''}
                variant="outlined"
                onChange={this.changeValue}
                onFocus={event => { event.target.select(); }}
                inputProps={{maxLength:"32"}}
                error={this.state.usernameValid === false}
                sx={{ width: 140 }}
              />
              :
              username
            }            
            &nbsp;
            <PasswordIcon fontSize="small" color="error" sx={{ marginTop: userMargin }}/>&nbsp;
            {this.state.edit && !this.state.locationsOnlyMode ? 
              <TextField
                id="password"
                label="Password"
                value={password || ''}
                variant="outlined"
                onChange={this.changeValue}
                onFocus={event => { event.target.select(); }}
                inputProps={{maxLength:"32"}}
                error={this.state.passwordValid === false}
                sx={{ width: 140 }}
              />
              :
              password
            }
            <IconButton aria-label="Generate" onClick={() => {navigator.clipboard.writeText(username_pass)}} sx={{marginTop: 0, marginLeft: 0}}>
              <ContentCopyIcon fontSize="small" titleAccess="Copy to clipboard"/>
            </IconButton><br/>


            <CreditCardIcon fontSize="small" color="primary" sx={{ marginTop: cardMargin }}/>&nbsp;
            {this.state.edit && !this.state.locationsOnlyMode ? 
              <TextField
                id="card"
                label="Card"
                value={card || ''}
                variant="outlined"
                onChange={this.changeValue}
                onFocus={event => { event.target.select(); }}
                inputProps={{maxLength:"32"}}
                error={this.state.cardValid === false}
                sx={{ width: 140, marginTop: 1 }}
              />
              :
              card
            }
            &nbsp;
            <PinIcon fontSize="small" color="error" sx={{ marginTop: cardMargin }}/>&nbsp;
            {this.state.edit && !this.state.locationsOnlyMode ? 
              <TextField
                id="pin"
                label="Pin"
                value={pin || ''}
                variant="outlined"
                onChange={this.changeValue}
                onFocus={event => { event.target.select(); }}
                inputProps={{maxLength:"32"}}
                error={this.state.pinValid === false}
                sx={{ width: 140, marginTop: 1 }}
              />
              :
              pin
            }
            <IconButton aria-label="Generate" onClick={() => {navigator.clipboard.writeText(card_pin)}} sx={{marginTop: 0, marginLeft: 0}}>
              <ContentCopyIcon fontSize="small" titleAccess="Copy to clipboard"/>
            </IconButton>
          </CardActions>
          
        </Box>
      </Box>       
          
      <Box sx={{ flexDirection: 'row', width: '100%', display: 'flex' }}>
        <Box sx={{ flexDirection: 'column', justifyContent:"left", width: '100%' }}>
          <CardActions disableSpacing sx={{display: "flex", textAlign: "start", justifyContent:"left"}}>
            <UpdateIcon size="small" titleAccess="Modified"/>&nbsp;
            {modifiedOn},
            <TokenIcon size="small" titleAccess="Token Valid"/>&nbsp;
            {tokenValid}
          </CardActions>          
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', textAlign: "end", justifyContent:"right", alignItems: "right", paddingTop: "8px" }}>
          {saveButtonVisible ?
            <Button
              variant="contained"
              size="small"
              sx={{ color: "#e3e3e5" }}
              startIcon={<SaveIcon />}
              onClick={this.pushData}
            >
              Save
            </Button>
            : ""
          }
        </Box>
        {this.state.locationsOnlyMode && (
          <IconButton
            aria-label="Cancel locations edit"
            title="Cancel locations edit"
            onClick={this.cancelLocationsOnlyMode}
            sx={{ ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Box sx={{ flexDirection: 'column', justifyContent:"right", width: '100%' }}>
          <CardActions disableSpacing sx={{display: "block", textAlign: "end" }}>                
           {/*  {username}, {card}    */}
          <ExpandMore
            expand={this.state.expanded}
            onClick={this.handleExpandClick}
            aria-expanded={this.state.expanded}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </ExpandMore>
          </CardActions>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Collapse in={this.state.expanded} timeout="auto" unmountOnExit sx={{width: "100% !important"}}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'row', textAlign: "middle", justifyContent:"center", alignItems: "center", paddingTop: "8px"}}>
              {(this.state.edit || this.state.locationsOnlyMode) ?
                <FormControl sx={{ m: 1, width: "80%" }}>
                  <InputLabel id="locationsSelect">Locations</InputLabel>
                  <Select
                    labelId="locationsSelect"
                    id="locationsCheckBox"
                    multiple
                    value={locationNames}
                    onChange={this.changeLocation}
                    input={<OutlinedInput label="Locations" />}                  
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: "90%", maxHeight: 110, overflowY: 'auto', alignItems: 'flex-start' }}>
                        {selected.map((value, i) => (
                          <Chip key={(value || `sel-${i}`)} label={value} />
                        ))}
                      </Box>
                    )}                  
                      /* selected.join(', ')} */
                    sx={{ minWidth: "300px", maxWidth: "100%", width: "800px" }}
                    MenuProps={LocationSelectMenuProps}
                  >
                    {locations.map((location) => (
                      <MenuItem key={location} value={location}>
                        <Checkbox checked={locationNames.indexOf(location) > -1} />
                        <ListItemText primary={location} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                :
                <TableContainer sx={{ width: '100%', maxHeight: "45vh", overflowX: 'auto', overflowY: "auto", }}>
                  <Table stickyHeader sx={{ minWidth: 700, textAlign: "middle", justifyContent:"center" }} aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>
                          <IconButton
                            aria-label="Edit Locations Only"
                            title="Edit Locations Only"
                            onClick={this.toggleLocationsOnlyMode}
                          >
                            <Avatar alt="Edit Locations Only" sx={{ width: 24, height: 24, color: "#273442" }}>
                              {this.state.locationsOnlyMode ? <FaMinusCircle /> : <FaPlusCircle />}
                            </Avatar>
                          </IconButton>
                          {/* Locations */}
                        </StyledTableCell>
                        <StyledTableCell align="left">Name</StyledTableCell>
                        <StyledTableCell align="left">Company</StyledTableCell>            
                        <StyledTableCell align="left">Address</StyledTableCell>            
                        {/* <StyledTableCell align="left">City</StyledTableCell>
                        <StyledTableCell align="left">County</StyledTableCell>
                        <StyledTableCell align="left">Country</StyledTableCell>
                        <StyledTableCell align="left">Region</StyledTableCell> */}
                        <StyledTableCell align="left">Level</StyledTableCell>
                        <StyledTableCell align="right">Action</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allData || null}
                    </TableBody>
                  </Table>
                </TableContainer>
              }   
            </Box>
          </CardContent>

        </Collapse>

      </Box>
    </Card>
   
    );

  }
}

export default Employee;
